import { useState, useEffect } from 'react';
import { Box, Button, Dialog, DialogTitle, DialogContent, DialogActions, Typography, TextField, MenuItem, Select, Paper, Stack, Tooltip } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { Edit as EditIcon, Add as AddIcon, Cancel as CancelIcon } from '@mui/icons-material';

import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../store/store';
import { getAllHelpItems, saveHelpItem, updateHelpItem, deleteHelpItem, setOpen } from '../../store/helpAndResources.slice';
import IHelpItem, { HelpItem } from '../../Models/HelpAndResources/HelpItem.model';
import { HelpItemTypeEnum } from '../../Enum/HelpItemTypeEnum';
import ConfirmDelete from '../Shared/ConfirmDelete';

const AdminHelpResources = ({ title }: { title: string }) => {
    const dispatch = useAppDispatch();
    const { allHelpItems, isFetchingHelpItem, open } = useSelector((state: any) => state.helpAndResources);

    const [editingId, setEditingId] = useState<string | null>(null);
    // const [deletingId, setDeletingId] = useState<string | null>(null);
    const [showConfirmDelete, setShowConfirmDelete] = useState(false);
    const [formData, setFormData] = useState<IHelpItem>(new HelpItem());

    useEffect(() => {
        dispatch(getAllHelpItems());
        document.title = title;
    }, [dispatch, title]);

    const handleOpenDialog = (item?: IHelpItem) => {
        dispatch(setOpen(true));
        if (item) {
            setEditingId(item.Id);
            setFormData({ ...item });
        } else {
            setEditingId(null);
            setFormData(new HelpItem());
        }
    };

    const handleClose = () => {
        dispatch(setOpen(false));
        setEditingId(null);
    };

    const handleDelete = (item: IHelpItem) => {
        setFormData(item);
        setShowConfirmDelete(true);
    };

    const handleConfirmDelete = () => {
        if (formData.Id) {
            dispatch(deleteHelpItem(formData.Id));
        }
        setShowConfirmDelete(false);
    };

    const handleSave = () => {
        if (editingId) {
            dispatch(updateHelpItem(formData));
        } else {
            dispatch(saveHelpItem(formData));
        }
    };

    const columns: GridColDef[] = [
        { field: 'Title', headerName: 'Title', minWidth: 300 },
        { field: 'Description', headerName: 'Description', flex: 1, minWidth: 300 },
        { field: 'Type', headerName: 'Type', width: 120, valueGetter: (value, row) => HelpItemTypeEnum[row.Type] },
        { field: 'Url', headerName: 'URL', width: 200 },
        {
            field: 'action',
            headerName: 'Action',
            sortable: false,
            minWidth: 160,
            renderCell: (params) => {
                return (
                    <>
                        <Tooltip title="Edit" placement="left">
                            <EditIcon color="primary" style={{ marginLeft: '24px', cursor: 'pointer' }} onClick={() => handleOpenDialog(params.row)} />
                        </Tooltip>
                        <Tooltip title="Delete" placement="right">
                            <CancelIcon color="error" style={{ marginLeft: '24px', cursor: 'pointer' }} onClick={() => handleDelete(params.row)} />
                        </Tooltip>
                    </>
                );
            },
        },
    ];

    return (
        <Box sx={{ p: 4 }}>
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h5" gutterBottom fontWeight="600" color="primary">
                    Admin Help & Resources
                </Typography>
                <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog()} disabled={isFetchingHelpItem}>
                    {isFetchingHelpItem ? 'Processing...' : 'Add New Resource'}
                </Button>
            </Box>

            <Paper sx={{ height: 600, width: '100%', mb: 4 }}>
                <DataGrid
                    rows={allHelpItems}
                    columns={columns}
                    pageSizeOptions={[5, 10, 25]}
                    loading={isFetchingHelpItem}
                    getRowId={(row) => row.Id}
                    sx={{
                        border: 'none',
                        '& .MuiDataGrid-cell:hover': {
                            color: 'primary.main',
                        },
                    }}
                />
            </Paper>
            <ConfirmDelete
                open={showConfirmDelete}
                onClose={() => setShowConfirmDelete(false)}
                name={formData.Title}
                delete={handleConfirmDelete}
                cancel={() => setShowConfirmDelete(false)}
            />
            <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
                <DialogTitle>{editingId ? 'Edit Resource' : 'Add New Resource'}</DialogTitle>
                <DialogContent>
                    <Stack spacing={3} sx={{ mt: 1 }}>
                        <TextField fullWidth label="Title" value={formData.Title} onChange={(e) => setFormData({ ...formData, Title: e.target.value })} />
                        <TextField
                            fullWidth
                            multiline
                            rows={3}
                            label="Description"
                            value={formData.Description}
                            onChange={(e) => setFormData({ ...formData, Description: e.target.value })}
                        />
                        <TextField
                            fullWidth
                            label="URL"
                            disabled={editingId && formData.Type === HelpItemTypeEnum.DOCUMENT ? true : false}
                            type="url"
                            placeholder="https://..."
                            value={formData.Url}
                            onChange={(e) => setFormData({ ...formData, Url: e.target.value })}
                        />
                        <Box>
                            <Typography variant="caption" sx={{ mb: 1, display: 'block' }}>
                                Resource Type
                            </Typography>
                            <Select
                                fullWidth
                                disabled={editingId ? true : false}
                                value={formData.Type}
                                onChange={(e) => setFormData({ ...formData, Type: e.target.value as HelpItemTypeEnum })}
                            >
                                <MenuItem disabled value={HelpItemTypeEnum.DOCUMENT}>
                                    Document (PDF/Web)
                                </MenuItem>
                                <MenuItem value={HelpItemTypeEnum.VIDEO}>Video (YouTube/Vimeo)</MenuItem>
                            </Select>
                        </Box>
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ p: 3 }}>
                    <Button onClick={handleClose} variant="outlined" color="primary">
                        Cancel
                    </Button>
                    <Button onClick={handleSave} variant="contained" color="primary" disabled={isFetchingHelpItem}>
                        {editingId ? 'Update Resource' : 'Create Resource'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default AdminHelpResources;
